import { useEffect, useState } from 'react';
import { ERequestIds } from 'react-memory-optimization/dist/lib/binary/types';

import AmountInput from 'components/ui/Input/AmountInput';
import PhoneInput from 'components/ui/Input/Phone';

import { useZustandFormsStore } from 'stores/money/forms/store';
import { useZustandPaymentsStore } from 'stores/money/payments/store';
import { setPaymentsCurrentStepAction } from 'stores/money/payments/selectors';
import { getUserCurrencyIdSelector } from 'stores/user/personal/selectors';
import { useZustandUserStore } from 'stores/user/personal/store';
import { setFormsInfoAction } from 'stores/money/forms/selectors';
import { Steps } from 'stores/money/payments/types';

import { PaymentChannelType } from 'react-memory-optimization/dist/lib/store/user/types';

import { isFilledForm } from 'utils/validation';

import styles from '../Withdraw.module.css';

type Props = {
  min: number;
  max: number;
  phoneData?: {
    countryCodeDefault: string;
    defaultValue: string;
  };
  channelId:
    | PaymentChannelType.PlixoCreedoBkash
    | PaymentChannelType.PlixoCreedoEasyPaisa
    | PaymentChannelType.PlixoCreedoJazzCash
    | PaymentChannelType.PlixoCreedoNagad;
};

const PlixoCreedoWithdraw = (props: Props) => {
  const { min, max, channelId } = props;

  const setFormInfo = useZustandFormsStore(setFormsInfoAction);

  const [phone, setPhone] = useState<string>('');

  const [phoneErrorText, setPhoneErrorText] = useState<string>('');

  const setStep = useZustandPaymentsStore(setPaymentsCurrentStepAction);

  const currencyId = useZustandUserStore(getUserCurrencyIdSelector);

  const [amount, setAmount] = useState('');

  const handleChangePhone = (value: string) => {
    const validation = getPhoneValidation(value, channelId);

    setPhone(value);
    setPhoneErrorText(validation.isValid || !value ? '' : 'errors.invalidData');
  };

  const getPhoneValidation = (
    value: string,
    channelId: number
  ): { isValid: boolean; phoneNumber: string } => {
    const phoneWithoutCountryCode = value.slice(3);

    const phoneNumber = phoneWithoutCountryCode.startsWith('0')
      ? phoneWithoutCountryCode
      : `0${phoneWithoutCountryCode}`;

    switch (channelId) {
      case PaymentChannelType.PlixoCreedoBkash:
      case PaymentChannelType.PlixoCreedoNagad:
        return {
          isValid: /^01\d{9}$/.test(phoneNumber), // 01 + 9 цифр = 11 цифр
          phoneNumber,
        };

      case PaymentChannelType.PlixoCreedoEasyPaisa:
      case PaymentChannelType.PlixoCreedoJazzCash:
        return {
          isValid: /^03\d{9}$/.test(phoneNumber), // 03 + 9 цифр = 11 цифр
          phoneNumber,
        };

      default:
        return { isValid: true, phoneNumber };
    }
  };

  useEffect(() => {
    const data = [amount, phone];

    const isFilled = isFilledForm(data);

    const adaptedPhone = phone.slice(3);

    if (isFilled && currencyId && !phoneErrorText) {
      setFormInfo({
        isDeposit: false,
        form: {
          amount,
          phone:
            PaymentChannelType.PlixoCreedoEasyPaisa !== channelId
              ? adaptedPhone
              : `0${adaptedPhone}`,
          channelId,
          currencyId,
          requestId: ERequestIds.WithdrawRequest,
        },
      });

      setStep(Steps.FilledForm);
    } else {
      setStep(Steps.Amount);
      setFormInfo(null);
    }
  }, [amount, currencyId, channelId, phone, phoneErrorText]);

  const countryCode = [
    PaymentChannelType.PlixoCreedoEasyPaisa,
    PaymentChannelType.PlixoCreedoJazzCash,
  ].includes(channelId)
    ? 'PK'
    : 'BD';

  return (
    <div>
      <AmountInput
        isWithdraw
        placeholder="walletPopUp.forms.amount"
        inputStyleType="border-colorful"
        inputLabelType="labelUp"
        size="full"
        onChange={setAmount}
        isPresets={false}
        min={min}
        max={max}
        wrapperClasses={styles.marginInputBottom}
      />

      <PhoneInput
        countryCodeDefault={countryCode}
        withConfirm={false}
        inputStyleType="border-colorful"
        inputLabelType="labelUp"
        size="full"
        sendKey="09x"
        onChange={handleChangePhone}
        errorText={phoneErrorText}
        classesWrapper={styles.marginInputBottom}
        placeholder="walletPopUp.forms.phone"
      />
    </div>
  );
};

export default PlixoCreedoWithdraw;
